import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Compass, Radar, Send, MapPin, ArrowLeft, RefreshCw, ThumbsUp, ArrowRight,
  Loader2, Sparkles, LayoutGrid, Volume2, VolumeX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";
import BackButton from "@/components/hola/BackButton";
import { getDeviceId, ensureAnonUser } from "@/lib/anonSession";
import { guideStream } from "@/lib/guideStream";
import api from "@/lib/api";
const uid = () => Math.random().toString(36).slice(2, 10);

/* ============================================================
 * GuideVirtualPage — Modo "Guía Virtual" (tour guide by AI).
 * Al caminar dentro de una plaza/centro comercial, la IA Aura
 * presenta comercios cercanos uno por uno y pregunta si quieres
 * entrar. GPS-driven.
 * ============================================================ */

export default function GuideVirtualPage() {
  const [deviceId, setDeviceId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, text, merchant_name?}
  const [partial, setPartial] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [currentMerchant, setCurrentMerchant] = useState(null); // {merchant_id, business_name, ...}
  const [awaitingInterest, setAwaitingInterest] = useState(false); // muestra los quick replies
  const [gps, setGps] = useState(null); // {lat, lng}
  const [gpsError, setGpsError] = useState(null);
  const [input, setInput] = useState("");
  // Catálogo del comercio actual (Aura lo referenciará con [IMG:N])
  const [catalog, setCatalog] = useState([]); // [{id,image,title,price,description}]
  const [preferences, setPreferences] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("hr_guide_prefs") || "[]");
    } catch { return []; }
  });
  const [prefsChosen, setPrefsChosen] = useState(() => {
    return localStorage.getItem("hr_guide_prefs_chosen") === "1";
  });
  const settings = useSettings();
  const [voiceOn, setVoiceOn] = useState(() => {
    return localStorage.getItem("hr_guide_voice") === "1";
  });
  const [voiceExpired, setVoiceExpired] = useState(false);
  const voiceRef = useRef({ on: false, expired: false });
  useEffect(() => { voiceRef.current = { on: voiceOn, expired: voiceExpired }; }, [voiceOn, voiceExpired]);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const watchIdRef = useRef(null);
  const lastNextTsRef = useRef(0);
  const abortRef = useRef(null);
  const seenMerchantIdsRef = useRef(new Set());
  const catalogCacheRef = useRef(new Map()); // merchant_id → items[] (evita re-fetch)
  const startedRef = useRef(false);

  // Inicializa device_id + GPS watch
  useEffect(() => {
    (async () => {
      const cached = getDeviceId();
      if (cached) {
        setDeviceId(cached);
      } else {
        const anon = await ensureAnonUser();
        setDeviceId(anon?.device_id || (typeof anon === "string" ? anon : null));
      }
    })();
    if (!("geolocation" in navigator)) {
      setGpsError("Tu navegador no soporta GPS.");
      return;
    }
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsError(null);
      },
      (err) => setGpsError(err.message || "No pudimos leer tu GPS"),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
    );
    watchIdRef.current = id;
    return () => {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []);

  // Autoscroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, partial]);

  const playVoice = useCallback(async (text, merchantId) => {
    try {
      // Los marcadores [IMG:N] NO se leen en voz alta
      const cleanText = (text || "").replace(/\[IMG:\d+\]/gi, "").replace(/\s{2,}/g, " ").trim();
      if (!cleanText) return;
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/pwa/guide/voice/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_id: deviceId,
          text: cleanText,
          merchant_id: merchantId || currentMerchant?.merchant_id || null,
        }),
      });
      if (res.status === 402) {
        // Cuota agotada — pasamos a modo chat
        const data = await res.json();
        setVoiceExpired(true);
        setVoiceOn(false);
        localStorage.setItem("hr_guide_voice", "0");
        toast.info(data.message || "Se agotaron tus 5 minutos de voz. Aura te sigue por chat.");
        return;
      }
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (_) { /* ignore */ }
      }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => URL.revokeObjectURL(url);
      audio.play().catch(() => { /* autoplay blocked; UI mostrará el toggle */ });
    } catch (e) {
      /* silencioso */
    }
  }, [deviceId, currentMerchant]);

  const sendToAI = useCallback(async ({ text, merchantId = null, showBubble = true }) => {
    if (!deviceId || streaming) return;
    if (showBubble) {
      setMessages((m) => [...m, { id: uid(), role: "user", text }]);
    }
    setPartial("");
    setStreaming(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    await guideStream({
      deviceId,
      message: text,
      merchantId,
      lat: gps?.lat,
      lng: gps?.lng,
      signal: ctrl.signal,
      onChunk: (t) => setPartial((p) => p + t),
      onDone: ({ full }) => {
        setMessages((m) => [...m, { id: uid(), role: "assistant", text: full }]);
        setPartial("");
        setStreaming(false);
        // Reproduce voz si el modo voz está activo
        if (voiceRef.current.on && !voiceRef.current.expired && full && full.trim()) {
          playVoice(full, merchantId);
        }
      },
      onError: (err) => {
        toast.error(typeof err === "string" ? err : "La guía se atoró un momento");
        setStreaming(false);
        setPartial("");
      },
      onMerchantPaused: (mid) => {
        // El comercio pausó Aura (toggle o 0 minutos). Sacamos la burbuja del sistema
        // y avanzamos con el recorrido — no invitamos a este comercio.
        setCurrentMerchant(null);
        setAwaitingInterest(false);
        setCatalog([]);
        setMessages((m) => [
          ...m,
          {
            id: uid(),
            role: "assistant",
            text: "🌸 Este comercio pausó mi presentación por ahora. Seguimos con el recorrido y te aviso cuando aparezca otro cerca.",
          },
        ]);
      },
    });
  }, [deviceId, streaming, gps, playVoice]);

  // Arranque de la conversación (una sola vez, DESPUÉS de elegir preferencias)
  useEffect(() => {
    if (!deviceId || startedRef.current || !prefsChosen) return;
    startedRef.current = true;
    sendToAI({
      text: "__INICIO__ Hola, acabo de salir a recorrer la ciudad. Preséntate como mi guía y dime cómo funciona el recorrido en 2-3 frases.",
      showBubble: false,
    });
  }, [deviceId, sendToAI, prefsChosen]);

  // Poll de comercio más cercano cada vez que cambia la ubicación (con throttle 8s)
  useEffect(() => {
    if (!deviceId || !gps || !prefsChosen) return;
    if (streaming) return; // no arrancar mientras Aura escribe (no quemar el cooldown)
    const now = Date.now();
    if (now - lastNextTsRef.current < 8000) return;
    lastNextTsRef.current = now;
    (async () => {
      try {
        const { data } = await api.post("/pwa/guide/next", {
          device_id: deviceId,
          lat: gps.lat,
          lng: gps.lng,
          preferences,
        });
        if (data?.merchant && data.merchant.merchant_id) {
          const mid = data.merchant.merchant_id;
          // Solo reaccionar si es un comercio distinto al que ya presentamos
          if (currentMerchant?.merchant_id === mid) return;
          if (seenMerchantIdsRef.current.has(mid)) return;
          seenMerchantIdsRef.current.add(mid);
          setCurrentMerchant({ ...data.merchant, offer: data.offer, distance_m: data.distance_m });
          setAwaitingInterest(true);
          // Fetch catálogo público del comercio para renderizar [IMG:N]
          // Cache local por merchant_id → si Aura vuelve a pasar por el mismo local, no re-descargamos
          const cached = catalogCacheRef.current.get(mid);
          if (cached) {
            setCatalog(cached);
          } else {
            api.get(`/public/merchants/${mid}/catalog`)
              .then(({ data: catRes }) => {
                const items = catRes?.items || [];
                catalogCacheRef.current.set(mid, items);
                setCatalog(items);
              })
              .catch(() => {
                catalogCacheRef.current.set(mid, []); // negative cache — no re-intentes
                setCatalog([]);
              });
          }
          const offerLine = data.offer?.title ? `. Su oferta actual es "${data.offer.title}"` : "";
          const distLine = data.distance_m != null ? ` (a ~${Math.round(data.distance_m)}m de ti)` : "";
          await sendToAI({
            text: `__NUEVO_COMERCIO__ Estoy cerca de ${data.merchant.business_name}${distLine}${offerLine}. Preséntamelo en 2-3 frases y pregúntame si me interesa conocerlo o pasamos al siguiente.`,
            merchantId: mid,
            showBubble: false,
          });
        }
      } catch (e) {
        // silencioso; la próxima tick lo reintenta
      }
    })();
  }, [gps, deviceId, streaming, currentMerchant, sendToAI, prefsChosen, preferences]);

  const replyYes = () => {
    setAwaitingInterest(false);
    // Marca interés en el backend para imputar el costo de IA a este comercio
    if (currentMerchant?.merchant_id && deviceId) {
      api.post("/pwa/guide/interest", {
        device_id: deviceId,
        merchant_id: currentMerchant.merchant_id,
        interested: true,
      }).catch(() => { /* silencioso */ });
    }
    sendToAI({
      text: `Sí, me interesa ${currentMerchant?.business_name || "este comercio"}. Cuéntame más y qué debería probar.`,
      merchantId: currentMerchant?.merchant_id,
    });
  };
  const replyNo = () => {
    setAwaitingInterest(false);
    sendToAI({
      text: "No, gracias. Pasamos al siguiente.",
      merchantId: currentMerchant?.merchant_id,
    });
    setCurrentMerchant(null);
  };

  const sendFree = () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    sendToAI({ text, merchantId: currentMerchant?.merchant_id });
  };

  // Consulta cuota de voz al arrancar
  useEffect(() => {
    if (!deviceId) return;
    api.get(`/pwa/guide/voice/quota?device_id=${deviceId}`)
      .then(({ data }) => setVoiceExpired(Boolean(data?.expired)))
      .catch(() => { /* silencioso */ });
  }, [deviceId]);

  const resetTour = async () => {
    if (abortRef.current) abortRef.current.abort();
    if (audioRef.current) { try { audioRef.current.pause(); } catch (_) { /* ignore */ } }
    seenMerchantIdsRef.current.clear();
    catalogCacheRef.current.clear();
    setCurrentMerchant(null);
    setCatalog([]);
    setAwaitingInterest(false);
    setMessages([]);
    setPartial("");
    setStreaming(false);
    startedRef.current = false;
    lastNextTsRef.current = 0;
    try {
      await api.post("/pwa/guide/reset", { device_id: deviceId, lat: gps?.lat || 0, lng: gps?.lng || 0 });
      toast.success("Recorrido reiniciado");
      // Re-arranque
      startedRef.current = true;
      sendToAI({
        text: "__INICIO__ Reiniciamos el recorrido. Salúdame de nuevo y dime que estás lista para acompañarme.",
        showBubble: false,
      });
    } catch (e) {
      toast.error("No se pudo reiniciar");
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendFree();
    }
  };

  // ---------- Preferences flow ----------
  const savePreferences = async (prefs) => {
    setPreferences(prefs);
    localStorage.setItem("hr_guide_prefs", JSON.stringify(prefs));
    localStorage.setItem("hr_guide_prefs_chosen", "1");
    setPrefsChosen(true);
    if (deviceId) {
      try {
        await api.post("/pwa/guide/prefs", { device_id: deviceId, preferences: prefs });
      } catch { /* silencioso */ }
    }
  };

  if (!prefsChosen) {
    return <PreferencesScreen initial={preferences} onDone={savePreferences} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] to-black text-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-black/80 backdrop-blur border-b border-white/10">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-2">
          <BackButton to="/scan" label="" testid="guide-back-btn" />
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-[#FF007A] grid place-items-center shrink-0">
              <Compass className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-extrabold text-sm leading-tight">Aura · Guía Virtual</div>
              <div className="text-[11px] text-neutral-400 truncate">
                {gpsError ? "GPS off" : gps ? (
                  currentMerchant ? `📍 ${currentMerchant.business_name}` : "Buscando comercios cerca…"
                ) : "Activando GPS…"}
              </div>
            </div>
          </div>
          <Link
            to="/radar"
            className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 hover:text-white flex items-center gap-1"
            data-testid="guide-switch-directory"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Directorio
          </Link>
          {settings.aura_voice_enabled && (
<button
            onClick={() => {
              const next = !voiceOn;
              setVoiceOn(next);
              localStorage.setItem("hr_guide_voice", next ? "1" : "0");
              if (!next && audioRef.current) { try { audioRef.current.pause(); } catch (_) { /* ignore */ } }
              if (next && voiceExpired) toast.error("Se agotaron tus 5 minutos de voz");
            }}
            disabled={voiceExpired}
            className={`w-9 h-9 rounded-full grid place-items-center transition-colors ${
              voiceOn ? "bg-[#FF007A] hover:bg-[#e6006e]" : "bg-white/5 hover:bg-white/10"
            } ${voiceExpired ? "opacity-40 cursor-not-allowed" : ""}`}
            aria-label={voiceOn ? "Apagar voz" : "Encender voz"}
            data-testid="guide-voice-toggle"
            title={voiceExpired ? "Voz agotada — usa el chat" : (voiceOn ? "Aura habla en voz alta" : "Encender voz de Aura")}
          >
            {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
)}
          <button
            onClick={() => { localStorage.removeItem("hr_guide_prefs_chosen"); setPrefsChosen(false); }}
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center"
            aria-label="Editar preferencias"
            data-testid="guide-edit-prefs-btn"
            title="Editar categorías"
          >
            <Sparkles className="w-4 h-4" />
          </button>
          <button
            onClick={resetTour}
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center"
            aria-label="Reiniciar recorrido"
            data-testid="guide-reset-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Merchant chip */}
      <AnimatePresence>
        {currentMerchant && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="max-w-md mx-auto w-full px-4 pt-3"
            data-testid="guide-current-merchant"
          >
            <div className="rounded-2xl bg-gradient-to-br from-[#FF007A]/25 to-[#0055FF]/25 border border-white/10 p-3 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-white/10 grid place-items-center shrink-0 overflow-hidden">
                {currentMerchant.picture ? (
                  <img src={currentMerchant.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Radar className="w-5 h-5" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-extrabold text-sm truncate">{currentMerchant.business_name}</div>
                <div className="text-[11px] text-neutral-400 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> ~{Math.round(currentMerchant.distance_m || 0)}m
                  {currentMerchant.offer?.title && <span className="ml-2 text-[#FF007A]">· {currentMerchant.offer.title}</span>}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto max-w-md mx-auto w-full px-4 py-4 space-y-3" data-testid="guide-messages">
        {messages.length === 0 && streaming && (
          <div className="flex items-center gap-2 text-neutral-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Aura te está saludando…
          </div>
        )}
        {messages.map((m) => <Bubble key={m.id} role={m.role} text={m.text} catalog={catalog} />)}
        {partial && <Bubble role="assistant" text={partial} streaming catalog={catalog} />}
      </div>

      {/* Quick replies */}
      {awaitingInterest && !streaming && (
        <div className="max-w-md mx-auto w-full px-4 pb-2 flex gap-2" data-testid="guide-quick-replies">
          <Button
            onClick={replyYes}
            className="flex-1 h-11 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold"
            data-testid="guide-yes-btn"
          >
            <ThumbsUp className="w-4 h-4" /> Sí, me interesa
          </Button>
          <Button
            onClick={replyNo}
            variant="outline"
            className="flex-1 h-11 rounded-full border-white/20 text-white hover:bg-white/10 font-bold"
            data-testid="guide-no-btn"
          >
            Siguiente <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Input */}
      <div className="max-w-md mx-auto w-full px-4 pb-5 pt-2 border-t border-white/10 flex items-center gap-2 bg-black">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder={streaming ? "Aura está escribiendo…" : "Escríbele a Aura…"}
          disabled={streaming || !deviceId}
          className="h-11 rounded-full bg-white/5 border-white/10 text-white placeholder:text-neutral-500"
          data-testid="guide-input"
        />
        <Button
          onClick={sendFree}
          disabled={streaming || !input.trim()}
          className="h-11 w-11 rounded-full bg-[#FF007A] hover:bg-[#e6006e] p-0 grid place-items-center"
          data-testid="guide-send-btn"
        >
          {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>

      {gpsError && (
        <div className="max-w-md mx-auto w-full px-4 pb-4">
          <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2">
            ⚠️ Necesitamos tu GPS para que Aura sepa dónde estás. Activa la ubicación en tu navegador y refresca.
          </div>
        </div>
      )}
    </div>
  );
}

function Bubble({ role, text, streaming, catalog = [] }) {
  const isUser = role === "user";
  const isSystemTrigger = !isUser && text?.startsWith?.("__");
  if (text?.startsWith?.("__INICIO__") || text?.startsWith?.("__NUEVO_COMERCIO__")) return null;

  // Parseo de [IMG:N] — extrae marcadores y los reemplaza por tarjetas de producto.
  // Máx 2 imágenes por burbuja (guardarraíl UI).
  const { cleanText, imgRefs } = parseImgMarkers(text || "", catalog);

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-[#FF007A] grid place-items-center mr-2 shrink-0">
          <Sparkles className="w-3.5 h-3.5 text-white" />
        </div>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[#0055FF] text-white rounded-br-sm"
            : "bg-white/10 text-white rounded-bl-sm"
        }`}
        data-testid={isUser ? "guide-user-msg" : "guide-assistant-msg"}
      >
        {isSystemTrigger ? "..." : cleanText}
        {streaming && <span className="inline-block w-1.5 h-3 ml-1 bg-white/60 animate-pulse rounded-sm align-middle" />}
        {imgRefs.length > 0 && (
          <div className="mt-2 grid grid-cols-1 gap-2" data-testid="guide-msg-images">
            {imgRefs.slice(0, 2).map((it) => (
              <ProductCard key={it.id || it.title} item={it} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function parseImgMarkers(text, catalog) {
  // Encuentra todos los [IMG:N] y los mapea al catálogo (1-indexed)
  const RE = /\[IMG:(\d+)\]/gi;
  const refs = [];
  const seen = new Set();
  let m;
  while ((m = RE.exec(text)) !== null) {
    const idx = parseInt(m[1], 10) - 1;
    if (idx < 0 || idx >= catalog.length) continue;
    if (seen.has(idx)) continue;
    seen.add(idx);
    refs.push(catalog[idx]);
  }
  return {
    cleanText: text.replace(RE, "").replace(/\s{2,}/g, " ").trim(),
    imgRefs: refs,
  };
}

function ProductCard({ item }) {
  return (
    <div className="rounded-xl overflow-hidden bg-black/40 border border-white/10" data-testid="guide-product-card">
      {item.image ? (
        <img src={item.image} alt={item.title || ""} className="w-full aspect-video object-cover" />
      ) : (
        <div className="w-full aspect-video bg-white/5 grid place-items-center text-white/40 text-xs">Sin foto</div>
      )}
      <div className="p-2.5">
        <div className="font-heading font-extrabold text-sm leading-tight">{item.title || "Producto"}</div>
        {item.price && (
          <div className="text-[#FF007A] font-bold text-xs mt-0.5">{item.price}</div>
        )}
        {item.description && (
          <div className="text-[11px] text-white/70 mt-1 line-clamp-2">{item.description}</div>
        )}
      </div>
    </div>
  );
}

// ---------- Preferences chooser ----------
const PREF_OPTIONS = [
  { key: "comida",           emoji: "🍔", label: "Comida y bebida",   desc: "Cafés, restaurantes, postres, bares" },
  { key: "ropa",             emoji: "👗", label: "Ropa y moda",       desc: "Boutiques, zapatos, joyería, accesorios" },
  { key: "tech",             emoji: "💻", label: "Tecnología",         desc: "Electrónica, celulares, gaming, gadgets" },
  { key: "belleza",          emoji: "💄", label: "Belleza y salud",   desc: "Farmacias, spa, estética, barbería" },
  { key: "hogar",            emoji: "🏠", label: "Hogar",              desc: "Muebles, decoración, cocina, jardín" },
  { key: "entretenimiento",  emoji: "🎮", label: "Entretenimiento",    desc: "Cine, arcade, juegos" },
  { key: "servicios",        emoji: "🏦", label: "Servicios",          desc: "Bancos, lavandería, papelería" },
];

function PreferencesScreen({ initial = [], onDone }) {
  const [selected, setSelected] = useState(new Set(initial));

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const finish = () => onDone(Array.from(selected));

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] to-black text-white flex flex-col" data-testid="guide-prefs-screen">
      <div className="max-w-md mx-auto w-full px-5 py-6 flex flex-col flex-1">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-[#FF007A] grid place-items-center shrink-0">
            <Compass className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">Guía virtual</div>
            <div className="font-heading font-extrabold text-xl">¿Qué buscas hoy?</div>
          </div>
        </div>

        <p className="text-sm text-neutral-400 leading-relaxed mb-5">
          Aura priorizará los comercios que más te interesen. Puedes elegir uno o varios — o dejarlo vacío para ver todo lo que aparezca.
        </p>

        <div className="grid grid-cols-2 gap-2.5 flex-1">
          {PREF_OPTIONS.map((opt) => {
            const active = selected.has(opt.key);
            return (
              <button
                key={opt.key}
                onClick={() => toggle(opt.key)}
                data-testid={`guide-pref-${opt.key}`}
                className={`text-left rounded-2xl border-2 p-3 transition-all ${
                  active
                    ? "border-[#FF007A] bg-[#FF007A]/15 shadow-md"
                    : "border-white/10 bg-white/5 hover:border-white/25"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{opt.emoji}</span>
                  {active && <span className="ml-auto w-5 h-5 rounded-full bg-[#FF007A] grid place-items-center text-[10px] font-bold">✓</span>}
                </div>
                <div className="font-heading font-extrabold text-sm mt-2 leading-tight">{opt.label}</div>
                <div className="text-[11px] text-neutral-400 mt-0.5 leading-tight">{opt.desc}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 space-y-2">
          <Button
            onClick={finish}
            className="w-full h-12 rounded-full bg-[#FF007A] hover:bg-[#e6006e] text-white font-extrabold"
            data-testid="guide-prefs-continue"
          >
            {selected.size === 0 ? "Mostrar todo el centro" : `Continuar con ${selected.size} ${selected.size === 1 ? "categoría" : "categorías"}`} <ArrowRight className="w-4 h-4" />
          </Button>
          <button
            onClick={() => onDone([])}
            className="w-full text-xs text-neutral-500 hover:text-white"
            data-testid="guide-prefs-skip"
          >
            Prefiero explorar todo sin filtro
          </button>
        </div>
      </div>
    </div>
  );
}
